import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  createInternalNote,
  createSupportMessage,
  deleteSupportAttachment,
  downloadSupportAttachment,
  fetchSupportAssignees,
  getSupportSla,
  getSupportTicket,
  getSupportTicketEvents,
  getSupportWatchers,
  markSupportTicketUnread,
  markSupportTicketViewed,
  uploadSupportAttachment,
} from '../api/authApi';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { colorTokens, radiusTokens } from '../app/designTokens';
import { ErrorState } from '../components/ErrorState';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { PlaceholderPanel } from '../components/PlaceholderPanel';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { SupportTicketWorkflowPanel } from '../components/SupportTicketWorkflowPanel';
import {
  supportTicketDisplayId,
  supportTicketSubject,
} from '../utils/supportTicketIdentity';
import { safeApiErrorMessage } from '../utils/safeApiErrorMessage';
import { formatSupportEventLabel } from '../utils/supportEventLabel';

const { semantic } = colorTokens;
const MAX_ATTACHMENT_MB = 10;
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf', 'txt', 'csv', 'xlsx', 'docx', 'zip'];
const TAB_CONVERSATION = 0;
const TAB_INTERNAL = 1;
const TAB_EVENTS = 2;
const TAB_ATTACHMENTS = 3;

function formatDate(value, emptyValue) {
  if (!value) return emptyValue;
  return new Date(value).toLocaleString();
}

function formatSize(bytes, emptyValue) {
  if (!bytes && bytes !== 0) return emptyValue;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileExtension(name) {
  const parts = String(name || '').split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
}

function validateLocalFile(file, t) {
  const maxBytes = MAX_ATTACHMENT_MB * 1024 * 1024;
  if (file.size > maxBytes) {
    return t('support:attachment_too_large', { max: MAX_ATTACHMENT_MB, name: file.name });
  }
  const ext = fileExtension(file.name);
  if (ext && !ALLOWED_EXTENSIONS.includes(ext)) {
    return t('support:attachment_type_denied', { name: file.name });
  }
  return null;
}

function sortMessagesChronological(messages) {
  return [...messages].sort((a, b) => {
    const aTime = a?.created_at ? new Date(a.created_at).getTime() : 0;
    const bTime = b?.created_at ? new Date(b.created_at).getTime() : 0;
    if (aTime !== bTime) return aTime - bTime;
    return Number(a?.id || 0) - Number(b?.id || 0);
  });
}

function messageAuthorName(message, t) {
  return (
    message.author_name
    || message.contractor?.name
    || message.contractor?.email
    || message.author_email
    || t('support:unknown_author')
  );
}

function MessageBubble({ message, onDownload, onDeleteAttachment, canDeleteAttachment, t, emptyValue }) {
  const isInternal = message.visibility === 'internal' || message.message_type === 'internal_note';
  const attachments = message.attachments || [];
  const authorLabel = isInternal
    ? t('support:actor_internal')
    : (message.author_type === 'system_user' || message.by_system
      ? t('support:actor_support')
      : t('support:actor_requester'));

  return (
    <Box
      data-testid={isInternal ? 'internal-note-bubble' : 'public-message-bubble'}
      sx={{
        p: 2,
        mb: 2,
        borderRadius: `${radiusTokens.card}px`,
        bgcolor: isInternal ? semantic.bg.surfaceSelected : semantic.bg.surface,
        border: `1px solid ${isInternal ? semantic.warning : semantic.border.subtle}`,
        borderLeftWidth: 3,
        borderLeftStyle: 'solid',
        borderLeftColor: isInternal ? semantic.warning : semantic.status.brand.border,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ color: semantic.text.primary }}>
          {messageAuthorName(message, t)}
        </Typography>
        <Chip label={authorLabel} size="small" color={isInternal ? 'warning' : 'default'} />
        {isInternal && <Chip label={t('support:attachments.internal')} size="small" color="warning" />}
        {message.by_system ? <Chip label={t('support:system')} size="small" /> : null}
        <Typography variant="caption" sx={{ color: semantic.text.muted }}>
          {formatDate(message.created_at, emptyValue)}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: semantic.text.secondary }}>
        {message.body || message.description}
      </Typography>
      {attachments.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 1.5 }}>
          {attachments.map((file) => (
            <Stack key={file.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button size="small" onClick={() => onDownload?.(file)} sx={{ textTransform: 'none', maxWidth: '100%' }}>
                <Typography variant="body2" noWrap component="span">
                  {file.original_filename || file.original_name || file.filename}
                </Typography>
              </Button>
              <Typography variant="caption" sx={{ color: semantic.text.muted }}>
                {formatSize(file.size_bytes, emptyValue)}
              </Typography>
              {canDeleteAttachment && (
                <Button size="small" color="error" onClick={() => onDeleteAttachment?.(file)}>
                  {t('support:attachments.delete')}
                </Button>
              )}
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}

export function SupportTicketDetailPage() {
  const { t } = useTranslation(['support', 'common']);
  const { id } = useParams();
  const { session } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState(TAB_CONVERSATION);
  const [replyText, setReplyText] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [sendingNote, setSendingNote] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [partialAttachmentPhase, setPartialAttachmentPhase] = useState(false);
  const [failedReplyFiles, setFailedReplyFiles] = useState([]);
  const [createdReplyMessageId, setCreatedReplyMessageId] = useState(null);
  const [sla, setSla] = useState(null);
  const [watchers, setWatchers] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [uploadVisibility, setUploadVisibility] = useState('public');
  const [uploading, setUploading] = useState(false);
  const [savingField, setSavingField] = useState(null);
  const [markUnreadBusy, setMarkUnreadBusy] = useState(false);
  const [confirmDeleteAttachment, setConfirmDeleteAttachment] = useState(null);
  const [deletingAttachment, setDeletingAttachment] = useState(false);
  const replyLockRef = useRef(false);
  const noteLockRef = useRef(false);
  const replyFileInputRef = useRef(null);
  const suppressAutoViewRef = useRef(false);
  const emptyValue = t('common:empty_value');

  const canReply = hasPermission(session, 'system.support.reply');
  const canAssign = hasPermission(session, 'system.support.assign');
  const canClose = hasPermission(session, 'system.support.close');
  const currentUserId = session?.user?.id;

  const refreshWatchers = async () => {
    try {
      const response = await getSupportWatchers(id);
      setWatchers(response.data || []);
    } catch {
      setWatchers([]);
    }
  };

  const refreshEventsIfOpen = () => {
    if (tab !== TAB_EVENTS) return;
    getSupportTicketEvents(id)
      .then((response) => setEvents(response.data || []))
      .catch(() => setEvents([]));
  };

  const loadTicket = () => {
    setLoading(true);
    setError(null);
    setNotFound(false);
    suppressAutoViewRef.current = false;

    getSupportTicket(id)
      .then((data) => {
        setTicket(data);
        if (!suppressAutoViewRef.current) {
          markSupportTicketViewed(id).catch(() => {});
        }
        getSupportSla(id).then(setSla).catch(() => setSla(null));
        refreshWatchers();
        fetchSupportAssignees().then(setAssignees).catch(() => setAssignees([]));
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 404 || status === 403) {
          setNotFound(true);
          setError(t('support:ticket_not_found'));
        } else {
          setError(t('support:load_ticket_failed'));
        }
        setTicket(null);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when route id changes
  }, [id]);

  useEffect(() => {
    if (tab !== TAB_EVENTS) return;
    getSupportTicketEvents(id)
      .then((response) => setEvents(response.data || []))
      .catch(() => setEvents([]));
  }, [id, tab]);

  const comments = useMemo(
    () => sortMessagesChronological(ticket?.comments || ticket?.messages || []),
    [ticket]
  );
  const publicMessages = useMemo(
    () => comments.filter((m) => m.visibility !== 'internal' && m.message_type !== 'internal_note'),
    [comments]
  );
  const internalNotes = useMemo(
    () => comments.filter((m) => m.visibility === 'internal' || m.message_type === 'internal_note'),
    [comments]
  );

  const canSubmitReply = Boolean(replyText.trim()) && !sendingReply && !partialAttachmentPhase;
  const canSubmitNote = Boolean(noteText.trim()) && !sendingNote;

  const handleReplyFilesSelected = (event) => {
    const selected = Array.from(event.target.files || []);
    if (!selected.length) return;
    const nextErrors = [];
    const accepted = [];
    selected.forEach((file) => {
      const localError = validateLocalFile(file, t);
      if (localError) nextErrors.push(localError);
      else accepted.push(file);
    });
    if (nextErrors.length) setActionError(nextErrors[0]);
    if (accepted.length) setReplyFiles((prev) => [...prev, ...accepted]);
    if (replyFileInputRef.current) replyFileInputRef.current.value = '';
  };

  const removeReplyFileAt = (index) => {
    setReplyFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFilesForMessage = async (messageId, fileList, visibility = 'public') => {
    const failed = [];
    for (const file of fileList) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('visibility', visibility);
        formData.append('message_id', String(messageId));
        await uploadSupportAttachment(id, formData);
      } catch {
        failed.push(file);
      }
    }
    return failed;
  };

  const handleReply = async () => {
    if (!canSubmitReply || replyLockRef.current) return;
    replyLockRef.current = true;
    setSendingReply(true);
    setActionError(null);

    try {
      const updated = await createSupportMessage(id, { body: replyText.trim() });
      setTicket(updated);
      const messages = updated?.comments || updated?.messages || [];
      const newest = sortMessagesChronological(messages).filter(
        (m) => m.visibility !== 'internal'
      ).at(-1);
      const messageId = newest?.id;
      setReplyText('');

      if (messageId && replyFiles.length) {
        const failed = await uploadFilesForMessage(messageId, replyFiles, 'public');
        if (failed.length) {
          setPartialAttachmentPhase(true);
          setFailedReplyFiles(failed);
          setCreatedReplyMessageId(messageId);
          setReplyFiles([]);
          setActionError(t('support:reply_attachments_partial'));
          const refreshed = await getSupportTicket(id);
          setTicket(refreshed);
          return;
        }
        setReplyFiles([]);
        const refreshed = await getSupportTicket(id);
        setTicket(refreshed);
      } else {
        setReplyFiles([]);
      }
    } catch {
      setActionError(t('support:send_reply_failed'));
    } finally {
      setSendingReply(false);
      replyLockRef.current = false;
    }
  };

  const handleRetryReplyAttachments = async () => {
    if (!createdReplyMessageId || !failedReplyFiles.length || replyLockRef.current) return;
    replyLockRef.current = true;
    setSendingReply(true);
    setActionError(null);
    try {
      const failed = await uploadFilesForMessage(createdReplyMessageId, failedReplyFiles, 'public');
      if (failed.length) {
        setFailedReplyFiles(failed);
        setActionError(t('support:reply_attachments_partial'));
      } else {
        setPartialAttachmentPhase(false);
        setFailedReplyFiles([]);
        setCreatedReplyMessageId(null);
      }
      const refreshed = await getSupportTicket(id);
      setTicket(refreshed);
    } catch {
      setActionError(t('support:attachments.uploadFailed'));
    } finally {
      setSendingReply(false);
      replyLockRef.current = false;
    }
  };

  const handleInternalNote = async () => {
    if (!canSubmitNote || noteLockRef.current) return;
    noteLockRef.current = true;
    setSendingNote(true);
    setActionError(null);
    try {
      const updated = await createInternalNote(id, { body: noteText.trim() });
      setTicket(updated);
      setNoteText('');
    } catch {
      setActionError(t('support:add_note_failed'));
    } finally {
      setSendingNote(false);
      noteLockRef.current = false;
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const localError = validateLocalFile(file, t);
    if (localError) {
      setActionError(localError);
      event.target.value = '';
      return;
    }
    setUploading(true);
    setActionError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('visibility', uploadVisibility);
      await uploadSupportAttachment(id, formData);
      const updated = await getSupportTicket(id);
      setTicket(updated);
    } catch (err) {
      const status = err?.response?.status;
      const mapped = safeApiErrorMessage(err, '');
      if (status === 422 && mapped) setActionError(mapped);
      else setActionError(t('support:attachments.uploadFailed'));
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDownload = async (attachment) => {
    try {
      const blob = await downloadSupportAttachment(id, attachment.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.original_filename || attachment.original_name || attachment.filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setActionError(t('support:attachments.downloadFailed'));
    }
  };

  const handleMarkUnread = async () => {
    if (markUnreadBusy) return;
    setMarkUnreadBusy(true);
    setActionError(null);
    suppressAutoViewRef.current = true;
    try {
      await markSupportTicketUnread(id);
    } catch {
      suppressAutoViewRef.current = false;
      setActionError(t('support:mark_unread_failed'));
    } finally {
      setMarkUnreadBusy(false);
    }
  };

  const handleTicketUpdated = (updated) => {
    setTicket(updated);
    setActionError(null);
    refreshEventsIfOpen();
  };

  const handleDeleteAttachment = async () => {
    if (!confirmDeleteAttachment || deletingAttachment || !canReply) return;
    setDeletingAttachment(true);
    setActionError(null);
    try {
      await deleteSupportAttachment(id, confirmDeleteAttachment.id);
      setConfirmDeleteAttachment(null);
      const refreshed = await getSupportTicket(id);
      setTicket(refreshed);
      refreshEventsIfOpen();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403 || status === 404) {
        setActionError(t('support:attachments.deleteDenied'));
      } else {
        setActionError(t('support:attachments.deleteFailed'));
      }
    } finally {
      setDeletingAttachment(false);
    }
  };

  if (loading) {
    return (
      <Box data-testid="ticket-detail-loading">
        <LoadingSkeleton type="Detail" />
      </Box>
    );
  }

  if (error || !ticket) {
    return (
      <ErrorState
        message={error || t('support:ticket_not_found')}
        onRetry={notFound ? undefined : loadTicket}
      />
    );
  }

  const displayId = supportTicketDisplayId(ticket);
  const subject = supportTicketSubject(ticket);
  const formFields = (ticket.form_fields || []).filter(
    (field) => field.key !== 'description' && field.key !== 'theme' && field.key !== 'subject'
  );
  const ticketFiles = (ticket.files || ticket.attachments || []).filter(
    (file) => file.upload_status !== 'deleted'
  );

  const contextPanel = (
    <SupportTicketWorkflowPanel
      ticket={ticket}
      displayId={displayId}
      emptyValue={emptyValue}
      formatDate={(value) => formatDate(value, emptyValue)}
      formFields={formFields}
      sla={sla}
      watchers={watchers}
      assignees={assignees}
      canReply={canReply}
      canAssign={canAssign}
      canClose={canClose}
      currentUserId={currentUserId}
      savingField={savingField}
      onSavingChange={setSavingField}
      onTicketUpdated={handleTicketUpdated}
      onWatchersRefresh={refreshWatchers}
      onError={setActionError}
      onMarkUnread={handleMarkUnread}
      markUnreadBusy={markUnreadBusy}
    />
  );

  return (
    <Box>
      <PageHeader
        type="Detail"
        title={displayId || t('support:ticket_number', { id: ticket.id })}
        subtitle={subject}
        backTo="/support"
        badge={(
          <Stack direction="row" spacing={1}>
            <StatusBadge status={ticket.status} category="support" />
            <StatusBadge status={ticket.priority} category="priority" />
          </Stack>
        )}
      />

      <Grid container spacing={3} direction={{ xs: 'column', md: 'row' }}>
        <Grid item xs={12} md={8} order={{ xs: 1, md: 1 }} data-testid="ticket-main-column">
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            sx={{ mb: 2 }}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            <Tab label={t('support:conversation')} />
            <Tab label={t('support:internal_notes')} />
            <Tab label={t('support:events')} />
            <Tab label={t('support:attachments.title')} />
          </Tabs>

          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {actionError}
            </Alert>
          )}

          {tab === TAB_CONVERSATION && (
            <Box>
              {ticket.description ? (
                <SectionCard
                  variant="Default"
                  title={t('support:initial_description')}
                  sx={{ mb: 2 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: semantic.text.secondary }}
                    data-testid="initial-description"
                  >
                    {ticket.description}
                  </Typography>
                </SectionCard>
              ) : null}

              <Typography variant="subtitle1" sx={{ mb: 1, color: semantic.text.primary }}>
                {t('support:public_conversation')}
              </Typography>
              {publicMessages.length === 0 ? (
                <Typography variant="body2" sx={{ mb: 2, color: semantic.text.muted }}>
                  {t('support:no_public_messages')}
                </Typography>
              ) : (
                publicMessages.map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    onDownload={handleDownload}
                    onDeleteAttachment={setConfirmDeleteAttachment}
                    canDeleteAttachment={canReply}
                    t={t}
                    emptyValue={emptyValue}
                  />
                ))
              )}

              <SectionCard variant="Default" title={t('support:tickets.publicReply')} sx={{ mb: 0 }}>
                <Box data-testid="reply-composer">
                {(ticket.status === 'resolved' || ticket.status === 'closed') && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    {t('support:reply_closed_hint')}
                  </Alert>
                )}
                <TextField
                  multiline
                  minRows={3}
                  fullWidth
                  value={replyText}
                  onChange={(e) => {
                    setReplyText(e.target.value);
                    setActionError(null);
                  }}
                  placeholder={t('support:reply_placeholder')}
                  disabled={sendingReply || partialAttachmentPhase}
                  sx={{ mb: 1 }}
                  inputProps={{ 'aria-label': t('support:reply_placeholder') }}
                />
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                  <Button variant="outlined" component="label" disabled={sendingReply || partialAttachmentPhase} size="small">
                    {t('support:add_reply_files')}
                    <input
                      ref={replyFileInputRef}
                      type="file"
                      hidden
                      multiple
                      onChange={handleReplyFilesSelected}
                    />
                  </Button>
                </Stack>
                {replyFiles.length > 0 && (
                  <List dense sx={{ mb: 1 }}>
                    {replyFiles.map((file, index) => (
                      <ListItem
                        key={`${file.name}-${index}`}
                        secondaryAction={(
                          <Button size="small" onClick={() => removeReplyFileAt(index)} disabled={sendingReply}>
                            {t('support:remove')}
                          </Button>
                        )}
                      >
                        <ListItemText
                          primary={file.name}
                          secondary={formatSize(file.size, emptyValue)}
                          primaryTypographyProps={{ noWrap: true }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
                {partialAttachmentPhase ? (
                  <Stack direction="row" spacing={1}>
                    <Button variant="contained" onClick={handleRetryReplyAttachments} disabled={sendingReply}>
                      {t('support:retry_failed_uploads')}
                    </Button>
                    <Button
                      onClick={() => {
                        setPartialAttachmentPhase(false);
                        setFailedReplyFiles([]);
                        setCreatedReplyMessageId(null);
                      }}
                      disabled={sendingReply}
                    >
                      {t('support:continue_to_ticket')}
                    </Button>
                  </Stack>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleReply}
                    disabled={!canSubmitReply}
                  >
                    {sendingReply ? t('support:sending_reply') : t('support:send_reply')}
                  </Button>
                )}
                </Box>
              </SectionCard>
            </Box>
          )}

          {tab === TAB_INTERNAL && (
            <Box data-testid="internal-notes-tab">
              {internalNotes.length === 0 ? (
                <Typography variant="body2" sx={{ mb: 2, color: semantic.text.muted }}>
                  {t('support:no_internal_notes')}
                </Typography>
              ) : (
                internalNotes.map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    onDownload={handleDownload}
                    onDeleteAttachment={setConfirmDeleteAttachment}
                    canDeleteAttachment={canReply}
                    t={t}
                    emptyValue={emptyValue}
                  />
                ))
              )}
              <SectionCard variant="Default" title={t('support:add_internal_note')} sx={{ mb: 0 }}>
                <TextField
                  multiline
                  minRows={2}
                  fullWidth
                  value={noteText}
                  onChange={(e) => {
                    setNoteText(e.target.value);
                    setActionError(null);
                  }}
                  placeholder={t('support:internal_note_placeholder')}
                  disabled={sendingNote}
                  sx={{ mb: 1 }}
                  inputProps={{ 'aria-label': t('support:internal_note_placeholder') }}
                />
                <Button
                  variant="outlined"
                  onClick={handleInternalNote}
                  disabled={!canSubmitNote}
                >
                  {sendingNote ? t('support:sending_note') : t('support:add_internal_note')}
                </Button>
              </SectionCard>
            </Box>
          )}

          {tab === TAB_EVENTS && (
            <Box>
              {events.length === 0 ? (
                <PlaceholderPanel title={t('support:events')} message={t('support:no_events')} />
              ) : (
                events.map((event) => {
                  const { label: eventLabel } = formatSupportEventLabel(event, t);

                  return (
                    <Box
                      key={event.id}
                      data-testid="support-event-row"
                      sx={{
                        p: 2,
                        mb: 1,
                        borderRadius: `${radiusTokens.card}px`,
                        bgcolor: semantic.bg.surface,
                        border: `1px solid ${semantic.border.subtle}`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ color: semantic.text.primary, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      >
                        {eventLabel}
                      </Typography>
                      <Typography variant="caption" sx={{ color: semantic.text.muted }} display="block">
                        {formatDate(event.created_at, emptyValue)} — {event.actor_label || event.actor_type || t('support:system')}
                      </Typography>
                      {event.event_type === 'notification_failed' && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          {t('support:notifications.failed')}
                        </Alert>
                      )}
                      {event.payload && String(event.event_type || '').startsWith('notification_') && (
                        <Typography variant="body2" sx={{ mt: 1, color: semantic.text.muted, wordBreak: 'break-word' }}>
                          {[event.payload.type, event.payload.recipient_type, event.payload.reason]
                            .filter(Boolean)
                            .join(' · ')}
                        </Typography>
                      )}
                    </Box>
                  );
                })
              )}
            </Box>
          )}

          {tab === TAB_ATTACHMENTS && (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
                <Button variant="outlined" component="label" disabled={uploading}>
                  {t('support:attachments.add')}
                  <input type="file" hidden onChange={handleUpload} />
                </Button>
                <TextField
                  select
                  size="small"
                  label={t('support:attachments.visibility')}
                  value={uploadVisibility}
                  onChange={(e) => setUploadVisibility(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{ minWidth: 140 }}
                >
                  <option value="public">{t('support:attachments.public')}</option>
                  <option value="internal">{t('support:attachments.internal')}</option>
                </TextField>
              </Stack>
              {ticketFiles.length === 0 ? (
                <PlaceholderPanel title={t('support:attachments.title')} message={t('support:no_files')} />
              ) : (
                ticketFiles.map((file) => (
                  <Box
                    key={file.id}
                    sx={{
                      p: 2,
                      mb: 1,
                      borderRadius: `${radiusTokens.card}px`,
                      bgcolor: semantic.bg.surface,
                      border: `1px solid ${semantic.border.subtle}`,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="subtitle2" sx={{ maxWidth: '100%', wordBreak: 'break-all', color: semantic.text.primary }}>
                        {file.original_filename || file.original_name || file.filename}
                      </Typography>
                      <Typography variant="caption" sx={{ color: semantic.text.muted }}>
                        {formatSize(file.size_bytes, emptyValue)} · {file.mime_type || emptyValue}
                      </Typography>
                      <Chip
                        size="small"
                        color={file.visibility === 'internal' ? 'warning' : 'default'}
                        label={file.visibility === 'internal' ? t('support:attachments.internal') : t('support:attachments.public')}
                      />
                      <Typography variant="caption" sx={{ color: semantic.text.muted }}>
                        {formatDate(file.created_at, emptyValue)}
                      </Typography>
                      <Button size="small" onClick={() => handleDownload(file)}>
                        {t('support:attachments.download')}
                      </Button>
                      {canReply && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setConfirmDeleteAttachment(file)}
                          data-testid={`delete-attachment-${file.id}`}
                        >
                          {t('support:attachments.delete')}
                        </Button>
                      )}
                    </Stack>
                  </Box>
                ))
              )}
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={4} order={{ xs: 2, md: 2 }}>
          {contextPanel}
        </Grid>
      </Grid>

      <Dialog
        open={Boolean(confirmDeleteAttachment)}
        onClose={() => !deletingAttachment && setConfirmDeleteAttachment(null)}
      >
        <DialogTitle>{t('support:attachments.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('support:attachments.deleteConfirm', {
              name: confirmDeleteAttachment?.original_filename
                || confirmDeleteAttachment?.original_name
                || confirmDeleteAttachment?.filename,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteAttachment(null)} disabled={deletingAttachment}>
            {t('common:cancel')}
          </Button>
          <Button color="error" onClick={handleDeleteAttachment} disabled={deletingAttachment}>
            {deletingAttachment ? t('support:attachments.deleting') : t('support:attachments.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
